(() => {
  if (window.__soweluSpeechAdapterV6Installed) return;

  const NativeRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!NativeRecognition) return;

  window.__soweluSpeechAdapterV6Installed = true;

  const normalize = (value = '') => String(value).replace(/\s+/g, ' ').trim();
  const comparableWords = (value = '') => normalize(value)
    .split(/\s+/)
    .map((word) => word.toLowerCase().replace(/^[^a-z0-9']+|[^a-z0-9']+$/g, ''))
    .filter(Boolean);

  const isWordPrefix = (prefixText, candidateText) => {
    const prefix = comparableWords(prefixText);
    const candidate = comparableWords(candidateText);
    if (!prefix.length || prefix.length > candidate.length) return false;
    return prefix.every((word, index) => word === candidate[index]);
  };

  const cloneResult = (result, transcriptOverride = null, finalOverride = null) => {
    const alternative = result?.[0] || {};
    const transcript = transcriptOverride === null
      ? String(alternative.transcript || '')
      : String(transcriptOverride || '');
    const confidence = Number.isFinite(alternative.confidence) ? alternative.confidence : 0;
    const cloned = [{ transcript, confidence }];
    cloned.isFinal = finalOverride === null ? Boolean(result?.isFinal) : Boolean(finalOverride);
    cloned.length = 1;
    return cloned;
  };

  function normalizeResultEvent(event) {
    const source = event?.results;
    if (!source || typeof source.length !== 'number') return event;

    const results = [];
    for (let i = 0; i < source.length; i += 1) {
      results[i] = cloneResult(source[i]);
    }

    let earliestChanged = Number.isInteger(event.resultIndex) ? event.resultIndex : 0;
    let previousFinalIndex = -1;

    for (let i = 0; i < results.length; i += 1) {
      const current = results[i];
      if (!current?.isFinal) continue;

      const currentText = normalize(current?.[0]?.transcript || '');
      if (!currentText) continue;

      if (previousFinalIndex >= 0) {
        const previous = results[previousFinalIndex];
        const previousText = normalize(previous?.[0]?.transcript || '');

        if (previousText && isWordPrefix(previousText, currentText)) {
          // Android/webkitSpeechRecognition can surface progressive cumulative
          // hypotheses as separate FINAL result slots ("hi" -> "hi I'm" -> ...).
          // Blank the superseded final slot and rewind resultIndex so the
          // controller deletes that slot instead of concatenating both finals.
          results[previousFinalIndex] = cloneResult(previous, '', true);
          earliestChanged = Math.min(earliestChanged, previousFinalIndex);
        } else if (previousText && isWordPrefix(currentText, previousText)) {
          // Defensive regression/replay: keep the longer prior final and blank
          // the shorter later slot.
          results[i] = cloneResult(current, '', true);
          earliestChanged = Math.min(earliestChanged, i);
          continue;
        }
      }

      previousFinalIndex = i;
    }

    return {
      resultIndex: earliestChanged,
      results
    };
  }

  class SoweluSpeechRecognitionV6 {
    constructor() {
      const native = new NativeRecognition();
      this._native = native;
      this._onstart = null;
      this._onresult = null;
      this._onerror = null;
      this._onend = null;

      native.onstart = (event) => this._onstart?.call(this, event);
      native.onerror = (event) => this._onerror?.call(this, event);
      native.onend = (event) => this._onend?.call(this, event);
      native.onresult = (event) => this._onresult?.call(this, normalizeResultEvent(event));
    }

    get lang() { return this._native.lang; }
    set lang(value) { this._native.lang = value; }

    get continuous() { return this._native.continuous; }
    set continuous(value) { this._native.continuous = value; }

    get interimResults() { return this._native.interimResults; }
    set interimResults(value) { this._native.interimResults = value; }

    get maxAlternatives() { return this._native.maxAlternatives; }
    set maxAlternatives(value) { this._native.maxAlternatives = value; }

    get onstart() { return this._onstart; }
    set onstart(handler) { this._onstart = typeof handler === 'function' ? handler : null; }

    get onresult() { return this._onresult; }
    set onresult(handler) { this._onresult = typeof handler === 'function' ? handler : null; }

    get onerror() { return this._onerror; }
    set onerror(handler) { this._onerror = typeof handler === 'function' ? handler : null; }

    get onend() { return this._onend; }
    set onend(handler) { this._onend = typeof handler === 'function' ? handler : null; }

    start() { return this._native.start(); }
    stop() { return this._native.stop(); }
    abort() { return this._native.abort(); }
  }

  // Voice V5/V6 resolves the constructor when its asset executes. Installing
  // this adapter first keeps the controller intact while normalizing the one
  // Android result-shape quirk at the API boundary.
  window.SpeechRecognition = SoweluSpeechRecognitionV6;
  window.webkitSpeechRecognition = SoweluSpeechRecognitionV6;
})();
